
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Ensures a profile exists for the user before uploading an image
 * @param userId The user's ID
 * @param accountType The account type ("supplier" or "retailer")
 * @returns Boolean indicating if profile exists or was created
 */
export const ensureProfileExists = async (
  userId: string, 
  accountType: "supplier" | "retailer"
): Promise<boolean> => {
  try {
    // Check if profile exists
    const { data: existingProfile, error: profileError } = await supabase
      .from("profiles")
      .select("id")
      .eq("id", userId)
      .single();

    if (profileError && profileError.code !== "PGRST116") {
      console.error("Error checking profile:", profileError);
      return false;
    }

    // If profile doesn't exist, create it
    if (!existingProfile) {
      const defaultValues = {
        id: userId,
        account_type: accountType,
        name: accountType === "supplier" ? "Supplier" : "Retailer",
      };
      
      // Add supplier-specific defaults
      const profileData = accountType === "supplier" 
        ? {
            ...defaultValues,
            minimum_free_shipping: 100,
            minimum_order: 0
          }
        : defaultValues;
      
      const { error: insertError } = await supabase
        .from("profiles")
        .insert(profileData);
      
      if (insertError) {
        console.error(`Error creating ${accountType} profile:`, insertError);
        throw insertError;
      }
    }
    
    return true;
  } catch (error: any) {
    console.error("Error ensuring profile exists:", error);
    toast({
      title: "Error",
      description: error.message || "Failed to create profile",
      variant: "destructive",
    });
    return false;
  }
};
